import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { all, put, select, take } from 'redux-saga/effects'
import type { RootState } from 'state/store'

import { createLogoutExtraReducers } from '@/utilities/redux'

import { Errors } from '../../util/validator'
import { StepName } from '../00-builder/state/builder-slice'

export type CompleteStepState = {
  approvals: {
    fees: boolean
    note: boolean
    portfolio: boolean
    strategy: boolean
    terms: boolean
    topupWithdraw: boolean
  }
  canProceed: boolean
  errors: Errors
}

const initialState: CompleteStepState = {
  canProceed: true,
  errors: {},
  approvals: {
    topupWithdraw: false,
    note: false,
    terms: false,
    portfolio: false,
    strategy: false,
    fees: false
  }
}

export const completeStep = createSlice({
  name: 'completeStep',
  initialState,
  reducers: {
    reset: state => {
      state.canProceed = true
    },
    setIsApproved: (state, action: PayloadAction<StepName | 'fees'>) => {
      const isCurrentlyApproved = state.approvals[action.payload]
      state.approvals[action.payload] = !isCurrentlyApproved
    },
    setUnapproved: (state, action: PayloadAction<StepName | 'fees'>) => {
      state.approvals[action.payload] = false
    },
    finish: () => {
      // dummy comment to keep linter happy
    },
    moveBack: () => {
      // dummy comment to keep linter happy
    }
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addMatcher(action => action.type === actionType, reducer)
    })
  }
})

export const { actions } = completeStep

export type CompleteActions = typeof actions

function* watchChangesAndInvalidateSaga() {
  while (true) {
    const action = yield take()
    const state: RootState = yield select()
    const { complete } = state

    if (complete.approvals.portfolio && action.type.startsWith('portfolio/')) {
      yield put(actions.setUnapproved('portfolio'))
    }
    if (complete.approvals.strategy && action.type.startsWith('strategyStep/setType')) {
      yield put(actions.setUnapproved('strategy'))
    }

    if (complete.approvals.fees && action.type === 'strategyStep/setType') {
      yield put(actions.setUnapproved('fees'))
    }
  }
}

export function* completeStepSaga() {
  yield all([watchChangesAndInvalidateSaga()])
}

export default completeStep.reducer
